import type {
  DebriefMetric,
  DebriefView,
  FeedMessage,
  HistoryItem,
  MissionBrief,
  MissionId,
  ObjectiveView,
  TerminalSnapshot
} from '@head-of-trading/contracts';
import type { MissionCatalogItem } from '../lib/api.js';
import type { Language } from './context.js';

interface MissionCopy {
  readonly title: string;
  readonly hook: string;
  readonly durationLabel: string;
  readonly skills: readonly string[];
  readonly brief: Omit<MissionBrief, 'missionId' | 'startAt' | 'endAt'>;
}

const MISSION_COPY: Readonly<Record<MissionId, MissionCopy>> = {
  M1: {
    title: 'The Mandate Shifts',
    hook: 'Build quickly, read the book, then reduce the position without letting stale orders decide for you.',
    durationLabel: '6 min',
    skills: ['Liquidity', 'Slippage', 'Adaptation'],
    brief: {
      title: 'The Mandate Shifts',
      eyebrow: 'EQUITY DERIVATIVES',
      narrative: 'An urgent strategic order has just arrived. Thomas Keller expects the position to be built quickly, but execution quality will be reviewed tick by tick.',
      role: "You are an index trader on Élise Moreau's desk. Execute the mandate without forcing a constantly evolving order book.",
      initialPortfolio: ['NAV: $100,000,000', 'NQ position: 0', 'Initial P&L: $0'],
      knownMandate: ['Reach 40 NQ by 10:03:00 ET', 'Tolerance: ±1 contract'],
      knownConstraints: ['Do not exceed the mandated position', 'Working orders remain your responsibility'],
      scoreDimensions: [
        { label: 'Initial mandate', weight: 25 },
        { label: 'Final mandate', weight: 20 },
        { label: 'Slippage', weight: 40 },
        { label: 'Adaptation', weight: 15 }
      ],
      instruments: ["NQ Dec '26 @CME"]
    }
  },
  M2: {
    title: 'Exit Without Being Seen',
    hook: 'Liquidate 400 NQ in a book that learns, widens, and retreats as your footprint turns toxic.',
    durationLabel: '12 min',
    skills: ['Market impact', 'Liquidity', 'Timing'],
    brief: {
      title: 'Exit Without Being Seen',
      eyebrow: 'CLIENT REDEMPTION',
      narrative: 'An unexpected client redemption requires a large position to be liquidated before the close. Bid-side liquidity is fragile, and the market learns from your actions.',
      role: 'You are responsible for the liquidation. The mandate is simple; the execution is not.',
      initialPortfolio: ['Long 400 NQ', 'NAV / collateral: $100,000,000', 'Initial P&L: $0'],
      knownMandate: ['Finish at exactly 0 NQ by 15:58:00 ET'],
      knownConstraints: ['Reduce-only sells', 'No short position allowed', 'Pause will become unavailable near the end'],
      scoreDimensions: [
        { label: 'Price and timing', weight: 50 },
        { label: 'Endogenous impact', weight: 50 }
      ],
      instruments: ["NQ Dec '26 @CME"]
    }
  },
  M3: {
    title: 'The Fed Bet',
    hook: 'Overweight tech without increasing beta, read the rates risk, and survive a Fed decision that defies the desk view.',
    durationLabel: '19 min',
    skills: ['Beta', 'Rates', 'Hedging'],
    brief: {
      title: 'The Fed Bet',
      eyebrow: 'FOMC DAY',
      narrative: 'Minutes before the Fed decision, the desk holds a strong conviction that rates will be cut. Maya Benali questions the consensus—and the hedge is yours to manage.',
      role: 'Overweight technology without increasing portfolio beta, then manage the risks you identify.',
      initialPortfolio: ['NAV: $100,000,000', 'SPY: $80,000,000', 'Cash: $20,000,000', 'Initial beta: 0.80'],
      knownMandate: ['Add $10,000,000 of tech exposure', 'Keep beta between 0.77 and 0.83', 'Do not trade SPY'],
      knownConstraints: ['FOMC decision at 14:00 ET', 'Rates positions are permitted', 'All rates positions must be closed before the end'],
      scoreDimensions: [
        { label: 'Initial construction', weight: 25 },
        { label: 'Rates hedge', weight: 30 },
        { label: 'Adaptation', weight: 30 },
        { label: 'Execution', weight: 15 }
      ],
      instruments: ['SPY @ARCA', 'NQ/MNQ and ES/MES @CME', 'ZQ @CBOT, SR1/SR3 @CME', 'ZT/ZF/ZN/ZB @CBOT']
    }
  }
};

const OBJECTIVE_TEXT: Readonly<Record<string, string>> = {
  'Attendez les instructions du desk.': 'Wait for instructions from the desk.',
  'Construire la position': 'Build the position',
  'Acheter NQ en lisant la liquidité disponible.': 'Buy NQ while reading the available liquidity.',
  '±1 contrat': '±1 contract',
  'Ne pas dépasser 40 NQ': 'Do not exceed 40 NQ',
  'Le mandat bascule': 'The Mandate Shifts',
  'Sortez sans être vu': 'Exit Without Being Seen',
  'Le pari de la Fed': 'The Fed Bet',
  'Ramener la position à la nouvelle cible et la tenir.': 'Bring the position back to the new target and hold it.',
  'Ne pas augmenter le risque au-delà de la position détenue': 'Do not increase risk beyond the current position',
  'Liquider la position': 'Liquidate the position',
  'Vendre la position sans provoquer une spirale d’impact.': 'Sell the position without triggering an impact spiral.',
  'Aucun short': 'No short position',
  'Position finale impérativement nulle': 'Final position must be flat',
  'Surpondérer la technologie': 'Overweight technology',
  'Construire l’exposition tech tout en maintenant le bêta du portefeuille.': 'Build the tech exposure while keeping portfolio beta within range.',
  '+10 M$ tech · bêta 0,77–0,83': '+$10m tech · beta 0.77–0.83',
  '±400 k$ sur la tech': '±$400k tech',
  'SPY inchangé': 'SPY unchanged',
  'Clôturer la couverture': 'Close the hedge',
  'Ramener toutes les positions de taux à zéro.': 'Bring all rates positions back to zero.',
  'DV01 taux : 0': 'Rates DV01: 0',
  'Aucune position de taux à la fin': 'No rates position at the end',
  'Réduire et stabiliser': 'Reduce and stabilize',
  'Ramener la surpondération tech à +4 M$ et tenir le bêta.': 'Reduce the tech overweight to +$4m and maintain beta.',
  '+4 M$ tech · bêta 0,77–0,83': '+$4m tech · beta 0.77–0.83',
  'Positions taux à zéro avant 14:04': 'Rates positions flat by 14:04'
};

const FEED_TEXT: Readonly<Record<string, string>> = {
  'Priorité absolue : établissez 40 NQ avant 10:03. Je veux la position, mais ne détruisez pas le prix.': 'Top priority: build a 40 NQ position by 10:03. I want the exposure, but do not destroy the price.',
  "Changement immédiat. Le comité réduit l'enveloppe : cible 25 NQ. Gérez vos ordres en cours et revenez dans la limite.": 'Immediate change. The committee is reducing the allocation: target 25 NQ. Manage your working orders and return within the limit.',
  "Rachat client confirmé. Liquidez les 400 NQ avant 15:58. Il n'y aura pas de délai supplémentaire.": 'Client redemption confirmed. Liquidate all 400 NQ by 15:58. There will be no extension.',
  'La profondeur acheteuse se fragmente. Les tailles visibles changent rapidement.': 'Bid-side depth is fragmenting. Displayed sizes are changing rapidly.',
  'Je veux +10 M$ de tech avant la Fed, sans augmenter le bêta du portefeuille. SPY ne bouge pas.': 'I want +$10m of tech exposure before the Fed without increasing portfolio beta. Do not trade SPY.',
  "Le marché price encore largement une baisse. Les dernières données d’inflation ne me rendent pas aussi confiante. Regardez les probabilités implicites avant l’annonce.": 'The market is still pricing a cut with high confidence. The latest inflation data make me less certain. Review the implied probabilities before the announcement.',
  'Statu quo hawkish. Clôturez maintenant toute la couverture de taux.': 'Hawkish hold. Close the entire rates hedge now.',
  'Prenez une partie des gains : réduisez la tech à +4 M$, bêta toujours entre 0,77 et 0,83.': 'Take some gains: reduce the tech overweight to +$4m while keeping beta between 0.77 and 0.83.',
  'Forte dégradation de la liquidité acheteuse': 'Severe deterioration in bid-side liquidity',
  'Federal Reserve : statu quo. Le communiqué adopte une tonalité plus restrictive qu’attendu.': 'Federal Reserve: no change. The statement is more hawkish than expected.',
  'Marge disponible faible.': 'Available margin is low.',
  'Risque de liquidation automatique.': 'Risk of automatic liquidation.',
  'Liquidation automatique déclenchée : marge de maintenance dépassée.': 'Automatic liquidation triggered: maintenance margin breached.',
  'Marge initiale restaurée. Contrôle rendu au trader.': 'Initial margin restored. Control returned to the trader.'
};

const ROLE_TEXT: Readonly<Record<string, string>> = {
  'Analyste macro': 'Macro Analyst',
  'Portfolio Manager': 'Portfolio Manager',
  'Head of Trading': 'Head of Trading',
  'Risk Manager': 'Risk Manager'
};

const FOMC_OUTCOME_TEXT: Readonly<Record<string, string>> = {
  'Baisse 25 pb': '25 bp cut',
  'Statu quo': 'No change'
};

const LABEL_TEXT: Readonly<Record<string, string>> = {
  'Terminée': 'Completed',
  'Abandonnée': 'Abandoned',
  'Incident technique': 'Technical incident',
  'Échec critique': 'Critical failure',
  'Mandat non atteint': 'Mandate not met',
  'Partiel': 'Partial',
  'Mandat atteint': 'Mandate met',
  'Très bon': 'Very good',
  'Remarquable': 'Outstanding'
};

const CAP_TEXT: Readonly<Record<string, string>> = {
  'Épisode critique de toxicité': 'Critical toxicity episode',
  'Épisode majeur ou effondrement répété': 'Major toxicity episode or repeated liquidity collapse',
  'Mandat 40 hors tolérance à 10:03': '40 NQ mandate outside tolerance at 10:03',
  'Mandat 25 hors tolérance à 10:06': '25 NQ mandate outside tolerance at 10:06',
  'Mandat initial manqué à 13:59:59': 'Initial mandate missed at 13:59:59',
  'Couverture prise dans le mauvais sens': 'Hedge established in the wrong direction',
  'Ratio de couverture hors 50–150 %': 'Hedge ratio outside 50–150%',
  'Exécution volontaire sur SPY': 'Voluntary SPY trade',
  'Valeur SPY hors tolérance à un checkpoint': 'SPY value outside tolerance at a checkpoint',
  'Réduction tech avant instruction': 'Tech exposure reduced before instruction',
  'Mandat final hors tolérance à 14:04': 'Final mandate outside tolerance at 14:04',
  'Risque de base supérieur à 30 %': 'Basis risk above 30%',
  'Risque de base supérieur à 15 %': 'Basis risk above 15%'
};

const ERROR_TEXT: Readonly<Record<string, string>> = {
  'Une erreur locale inattendue est survenue.': 'An unexpected local error occurred.',
  'Commande refusée.': 'Command rejected.',
  'Le contrôle a été repris dans une autre fenêtre.': 'Control was taken over in another window.',
  'Connexion au moteur indisponible. La commande n’a pas été envoyée.': 'The simulation engine is unavailable. The command was not sent.',
  'Origine non autorisée.': 'Origin not allowed.',
  'Session locale invalide.': 'Invalid local session.',
  'Loopback uniquement.': 'Loopback only.',
  'Jeton d’instance invalide.': 'Invalid instance token.',
  'Pseudo invalide : 3 à 24 lettres, chiffres, espaces, tirets ou underscores.': 'Invalid username: use 3 to 24 letters, numbers, spaces, hyphens, or underscores.',
  'Compte introuvable.': 'Account not found.',
  'Une tentative est déjà active.': 'An attempt is already active.',
  'Tentative introuvable.': 'Attempt not found.',
  'Compte non autorisé.': 'Account not authorized.',
  'La tentative est encore active.': 'The attempt is still active.',
  'Débrief stocké indisponible ; aucune reconstruction avec une autre version ne sera effectuée.': 'The stored debrief is unavailable; it will not be reconstructed with a different version.',
  'Route introuvable.': 'Route not found.',
  'Commande JSON invalide.': 'Invalid JSON command.',
  'Commande non conforme au protocole.': 'Command does not conform to the protocol.',
  'Contrat ou tentative invalide.': 'Invalid contract or attempt.',
  'Contrôle révoqué.': 'Control revoked.',
  'Une commande est déjà en traitement sur cet ordre.': 'A command is already being processed for this order.',
  'Débit de commandes dépassé.': 'Command rate limit exceeded.',
  'Trop de commandes simultanées.': 'Too many concurrent commands.',
  'Le serveur n’a pas retourné l’identifiant de la tentative.': 'The server did not return an attempt identifier.',
  'La tentative est terminée.': 'The attempt has ended.',
  'Trading indisponible pendant la liquidation automatique.': 'Trading is unavailable during automatic liquidation.',
  'Pause indisponible.': 'Pause is unavailable.',
  'La mission n’est pas en pause.': 'The mission is not paused.',
  'Vitesse verrouillée.': 'Speed is locked.',
  'Fin anticipée indisponible.': 'Early completion is unavailable.',
  'Trading indisponible pendant la pause.': 'Trading is unavailable while paused.',
  'Instrument non disponible dans cette mission.': 'This instrument is not available in this mission.',
  'Instrument indisponible.': 'Instrument unavailable.',
  'Quantité invalide.': 'Invalid quantity.',
  'Prix incompatible avec le tick.': 'Price is not aligned with the tick size.',
  'Échéance GTD invalide.': 'Invalid GTD expiry.',
  'Ordre croisé avec votre ordre actif': 'Order would cross your own working order.',
  'FOK non exécutable intégralement.': 'FOK order cannot be filled in full.',
  'Ordre non annulable.': 'Order cannot be canceled.',
  'Ordre non modifiable.': 'Order cannot be modified.',
  'Quantité inférieure aux exécutions déjà reçues.': 'Quantity is below the quantity already filled.',
  'La position est déjà à la cible communiquée.': 'The position is already at the stated target.',
  'Ordre incompatible avec le mandat courant.': 'Order is inconsistent with the current mandate.',
  'Quantité au-delà du mandat courant.': 'Quantity exceeds the current mandate.',
  'M2 accepte uniquement les ventes reduce-only de NQ.': 'M2 only accepts reduce-only NQ sell orders.',
  'La vente traverserait la position nulle.': 'The sale would take the position below zero.',
  'Historique des migrations SQLite inconnu ou modifié. Aucun changement appliqué.': 'Unknown or modified SQLite migration history. No changes were applied.',
  'Base SQLite non versionnée et non vide. Aucun changement appliqué.': 'The SQLite database is not versioned and is not empty. No changes were applied.',
  'Univers de graphiques incompatible avec le scénario.': 'Chart universe is incompatible with the scenario.',
  'Le shadow market contractuel est réservé à M2.': 'The contractual shadow market is reserved for M2.',
  'Divergence du hash de reprise': 'Resume-state hash mismatch',
  'snapshot incompatible': 'Incompatible snapshot',
  'Valeur varint invalide.': 'Invalid varint value.',
  'Delta signé invalide.': 'Invalid signed delta.',
  'Delta signé hors plage.': 'Signed delta out of range.',
  'Varint hors plage.': 'Varint out of range.',
  'Varint non canonique.': 'Non-canonical varint.',
  'Varint trop long.': 'Varint is too long.',
  'Varint tronqué.': 'Truncated varint.',
  'Prix de graphique invalide.': 'Invalid chart price.',
  'Prix de graphique tronqué.': 'Truncated chart price.',
  'Nombre de points de graphique invalide.': 'Invalid chart point count.',
  'Série de graphique invalide.': 'Invalid chart series.',
  'Point de graphique invalide.': 'Invalid chart point.',
  'Horodatage de graphique invalide.': 'Invalid chart timestamp.',
  'Prix compact hors plage.': 'Compact price out of range.',
  'Prix en ticks hors plage.': 'Tick price out of range.',
  'Série de graphique compacte invalide.': 'Invalid compact chart series.',
  'Base64 de graphique non canonique.': 'Non-canonical chart Base64.',
  'Horodatage de graphique non croissant.': 'Chart timestamps are not increasing.',
  'Horodatage de graphique hors plage.': 'Chart timestamp out of range.',
  'Marqueur de prix compact invalide.': 'Invalid compact price marker.',
  'Prix compact invalide.': 'Invalid compact price.',
  'Volume compact invalide.': 'Invalid compact volume.',
  'Point de graphique compact invalide.': 'Invalid compact chart point.',
  'Données superflues dans le graphique compact.': 'Unexpected trailing compact chart data.'
};

function missionTitle(missionId: MissionId, fallback: string): string {
  return MISSION_COPY[missionId]?.title ?? fallback;
}

function exact(value: string, catalogue: Readonly<Record<string, string>>): string {
  return catalogue[value] ?? value;
}

const displayLocale = (language: Language): 'en-US' | 'fr-FR' => language === 'fr' ? 'fr-FR' : 'en-US';

function parseDisplayNumber(raw: string): { readonly value: number; readonly digits: number; readonly explicitPlus: boolean } | null {
  const clean = raw.trim().replace(/[\s\u00a0\u202f]/g, '').replace('−', '-');
  const comma = clean.lastIndexOf(',');
  const dot = clean.lastIndexOf('.');
  const decimal = comma >= 0 && dot >= 0 ? (comma > dot ? ',' : '.') : comma >= 0 ? ',' : dot >= 0 ? '.' : null;
  const digits = decimal === null ? 0 : clean.length - clean.lastIndexOf(decimal) - 1;
  const normalized = comma >= 0 && dot >= 0
    ? comma > dot ? clean.replace(/\./g, '').replace(',', '.') : clean.replace(/,/g, '')
    : comma >= 0 ? clean.replace(',', '.') : clean;
  const value = Number(normalized);
  return Number.isFinite(value) ? { value, digits, explicitPlus: clean.startsWith('+') } : null;
}

function localizeFinancialText(text: string, language: Language): string {
  const locale = displayLocale(language);
  let localized = text.replace(/[+-]?\d+\.\d+/g, (raw) => {
    const parsed = parseDisplayNumber(raw);
    return parsed ? new Intl.NumberFormat(locale, { minimumFractionDigits: parsed.digits, maximumFractionDigits: parsed.digits }).format(parsed.value) : raw;
  });
  localized = localized.replace(/([+−-]?\d(?:[\d\s\u00a0\u202f.,]*\d)?)\s*%/gu, (full, raw: string) => {
    const parsed = parseDisplayNumber(raw);
    return parsed ? new Intl.NumberFormat(locale, { style: 'percent', minimumFractionDigits: parsed.digits, maximumFractionDigits: parsed.digits }).format(parsed.value / 100) : full;
  });
  localized = localized.replace(/([+−-]?\d(?:[\d\s\u00a0\u202f.,]*\d)?)\s*\$/gu, (full, raw: string) => {
    const parsed = parseDisplayNumber(raw);
    return parsed ? new Intl.NumberFormat(locale, {
      style: 'currency', currency: 'USD', currencyDisplay: 'narrowSymbol', signDisplay: parsed.explicitPlus ? 'always' : 'auto',
      minimumFractionDigits: parsed.digits, maximumFractionDigits: parsed.digits
    }).format(parsed.value) : full;
  });
  return localized;
}

function localizeObjective(objective: ObjectiveView): ObjectiveView {
  return {
    ...objective,
    title: exact(objective.title, OBJECTIVE_TEXT),
    instruction: exact(objective.instruction, OBJECTIVE_TEXT),
    target: exact(objective.target, OBJECTIVE_TEXT),
    tolerance: objective.tolerance === null ? null : exact(objective.tolerance, OBJECTIVE_TEXT),
    hardConstraints: objective.hardConstraints.map((constraint) => exact(constraint, OBJECTIVE_TEXT))
  };
}

function localizeFeedMessage(message: FeedMessage): FeedMessage {
  let text = exact(message.text, FEED_TEXT);
  const incident = /^Incident technique\s*:\s*(.+)$/u.exec(message.text);
  if (incident) text = `Technical incident: ${localizeErrorMessage(incident[1]!, 'en')}`;
  return {
    ...message,
    role: message.role === null ? null : exact(message.role, ROLE_TEXT),
    text,
    ...(message.action ? { action: { ...message.action, label: 'Open FOMC Watch' } } : {})
  };
}

function translateMetric(metric: DebriefMetric): DebriefMetric {
  switch (metric.id) {
    case 'm1-initial':
      return {
        ...metric,
        label: 'Initial mandate',
        rawValue: metric.rawValue.replace(/^(.* NQ) à (\d{2}:\d{2})$/u, '$1 at $2'),
        reference: metric.reference.replace('40 ±1 NQ', '40 NQ ±1')
      };
    case 'm1-final':
      return {
        ...metric,
        label: 'Final mandate',
        rawValue: metric.rawValue.replace(/^(.* NQ) à (\d{2}:\d{2})$/u, '$1 at $2'),
        reference: metric.reference.replace('25 ±1 NQ', '25 NQ ±1')
      };
    case 'm1-slip':
      return { ...metric, label: 'Slippage', reference: exact(metric.reference, { 'Midpoint avant arrivée': 'Pre-arrival midpoint' }) };
    case 'm1-adapt':
      return {
        ...metric,
        label: 'Adaptation',
        rawValue: exact(metric.rawValue, { 'Cible jamais atteinte': 'Target never reached' }),
        reference: exact(metric.reference, { 'Vitesse puis maintien': 'Speed, then hold' })
      };
    case 'm2-price':
      return {
        ...metric,
        label: 'Price and timing',
        rawValue: metric.rawValue.replace(/ pb vs VWAP$/u, ' bp vs. VWAP'),
        reference: metric.reference.replace('VWAP exogène', 'Exogenous VWAP')
      };
    case 'm2-impact':
      return {
        ...metric,
        label: 'Endogenous impact',
        rawValue: metric.rawValue.replace(/ pb$/u, ' bp'),
        reference: exact(metric.reference, { 'Shadow market sans impact': 'Impact-free shadow market' })
      };
    case 'm3-build':
      return {
        ...metric,
        label: 'Initial construction',
        rawValue: metric.rawValue.replace(/^(.+) M\$ · β (.+)$/u, '$$$1m · β $2'),
        reference: metric.reference.replace(/^\+10 M\$ · β 0,77–0,83$/u, '+$10m · β 0.77–0.83')
      };
    case 'm3-hedge':
      return { ...metric, label: 'Rates hedge' };
    case 'm3-adapt':
      return {
        ...metric,
        label: 'Adaptation',
        rawValue: metric.rawValue
          .replace(/^Taux à zéro en (.+)$/u, 'Rates flat in $1')
          .replace(/^Taux non clôturés$/u, 'Rates positions not closed')
          .replace(/^Couverture clôturée avant la Fed$/u, 'Hedge closed before the Fed'),
        reference: exact(metric.reference, { '+4 M$ tech puis maintien': '+$4m tech, then hold' })
      };
    case 'm3-execution':
      return {
        ...metric,
        label: 'Execution',
        rawValue: metric.rawValue.replace(/ pb · leg /u, ' bp · leg '),
        reference: exact(metric.reference, { 'Slippage, leg risk, obsolescence, base': 'Slippage, leg risk, stale orders, basis risk' })
      };
    default:
      return metric;
  }
}

function localizeMetric(metric: DebriefMetric, language: Language): DebriefMetric {
  const localized = language === 'en' ? translateMetric(metric) : { ...metric };
  return {
    ...localized,
    rawValue: localizeFinancialText(localized.rawValue, language),
    reference: localizeFinancialText(localized.reference, language)
  };
}

function translateLead(text: string): string {
  const exactLead: Readonly<Record<string, string>> = {
    'La nouvelle cible n’a jamais été atteinte.': 'The new target was never reached.',
    'La position est intégralement liquidée.': 'The position was fully liquidated.',
    'Les positions de taux n’ont pas été entièrement clôturées.': 'The rates positions were not fully closed.',
    'La couverture a été clôturée avant la décision de la Fed et n’a pas protégé le portefeuille.': 'The hedge was closed before the Fed decision and did not protect the portfolio.',
    'Cette tentative n’est pas comptabilisée à cause d’un incident technique.': 'This attempt is excluded because of a technical incident.',
    'La tentative a été abandonnée sans score.': 'The attempt was abandoned without a score.'
  };
  const translated = exact(text, exactLead);
  if (translated !== text) return translated;

  let match = /^Position initiale mesurée à (.+) NQ au checkpoint de (.+)\.$/u.exec(text);
  if (match) return `Initial position measured at ${match[1]} NQ at the ${match[2]} checkpoint.`;
  match = /^Slippage moyen : (.+) ticks sur (.+) contrats\.$/u.exec(text);
  if (match) return `Average slippage: ${match[1]} ticks across ${match[2]} contracts.`;
  match = /^Retour vers la nouvelle cible en (.+) secondes\.$/u.exec(text);
  if (match) return `Returned to the new target in ${match[1]} seconds.`;
  match = /^(.+) contrats vendus à un prix moyen de (.+)\.$/u.exec(text);
  if (match) return `${match[1]} contracts sold at an average price of ${match[2]}.`;
  match = /^Coût d’impact estimé face au shadow : (.+) pb\.$/u.exec(text);
  if (match) return `Estimated impact cost versus the shadow: ${match[1]} bp.`;
  match = /^Position résiduelle : (.+) NQ à (.+)\.$/u.exec(text);
  if (match) return `Residual position: ${match[1]} NQ at ${match[2]}.`;
  match = /^Exposition avant Fed : (.+) M\$, bêta (.+)\.$/u.exec(text);
  if (match) return `Pre-Fed exposure: $${match[1]}m, beta ${match[2]}.`;
  match = /^Couverture théorique au snapshot pré-Fed : (.+) %, soit (.+) \$ face à (.+) \$\.$/u.exec(text);
  if (match) return `Theoretical hedge at the pre-Fed snapshot: ${match[1]}%, or ${match[2]} $ versus ${match[3]} $.`;
  match = /^Positions de taux clôturées (.+) s après l’instruction ; cible finale atteinte en (.+) s\.$/u.exec(text);
  if (match) return `Rates positions closed ${match[1]}s after the instruction; final target reached in ${match[2]}s.`;
  match = /^Positions de taux clôturées (.+) s après l’instruction ; cible finale non atteinte\.$/u.exec(text);
  if (match) return `Rates positions closed ${match[1]}s after the instruction; final target not reached.`;
  match = /^Incident technique sans score\s*:\s*(.+)$/u.exec(text);
  if (match) return `Technical incident with no score: ${localizeErrorMessage(match[1]!, 'en')}`;
  return text;
}

function localizeLead(text: string, language: Language): string {
  return localizeFinancialText(language === 'en' ? translateLead(text) : text, language);
}

function translateBenchmark(benchmark: DebriefView['benchmarks'][number]): DebriefView['benchmarks'][number] {
  const labels: Readonly<Record<string, string>> = {
    'Ordre MKT immédiat': 'Immediate market order',
    'TWAP simple': 'Simple TWAP',
    'Shadow sans impact': 'Impact-free shadow',
    'Shadow market': 'Shadow market',
    'Sans couverture taux': 'No rates hedge',
    'Couverture ZQ de référence': 'Reference ZQ hedge',
    'Résultat du joueur': 'Trader result'
  };
  const values: Readonly<Record<string, string>> = {
    'Variable': 'Variable',
    'Référence': 'Reference',
    'Plafond 39': 'Capped at 39',
    'Non optimal': 'Suboptimal',
    '−144 000 $': '−$144,000',
    '+144 000 $': '+$144,000'
  };
  const details: Readonly<Record<string, string>> = {
    'Consomme la profondeur et déclenche une forte empreinte.': 'Consumes depth and creates a large footprint.',
    'Le lissage fixe ne s’adapte pas aux changements de carnet.': 'Fixed scheduling does not adapt to changes in the book.',
    'Même timing avec impact du joueur neutralisé.': 'Same timing with the trader’s market impact neutralized.',
    'Déclenche une toxicité majeure sur 400 contrats.': 'Triggers major toxicity across 400 contracts.',
    'Ignore les changements de spread et de profondeur.': 'Ignores changes in spread and depth.',
    'Contre-factuel : perte NQ relative à ES sous le choc hawkish.': 'Counterfactual: NQ loss relative to ES under the hawkish shock.',
    '192 ZQ Oct short portés pendant le choc, repère comparatif — les autres futures de taux restent valides.': '192 short Oct ZQ contracts held through the shock as a reference; other rates futures remain valid.'
  };
  let detail = exact(benchmark.detail, details);
  let match = /^Même journal sans impact endogène ; résiduel (.+) NQ valorisé au midpoint final\.$/u.exec(benchmark.detail);
  if (match) detail = `Same order log without endogenous impact; residual ${match[1]} NQ marked at the final midpoint.`;
  match = /^Hedge théorique (.+) \$ · P&L taux réalisé net (.+) \$ · slippage (.+) pb · commissions taux (.+) \$\.$/u.exec(benchmark.detail);
  if (match) detail = `Theoretical hedge ${match[1]} $ · net realized rates P&L ${match[2]} $ · slippage ${match[3]} bp · rates commissions ${match[4]} $.`;

  let value = exact(benchmark.value, values);
  match = /^(-?\d+(?:\.\d+)?) \$$/u.exec(benchmark.value);
  if (match) value = `${match[1]} $`;
  return { label: exact(benchmark.label, labels), value, detail };
}

function localizeBenchmark(benchmark: DebriefView['benchmarks'][number], language: Language): DebriefView['benchmarks'][number] {
  const localized = language === 'en' ? translateBenchmark(benchmark) : { ...benchmark };
  return {
    ...localized,
    value: localizeFinancialText(localized.value, language),
    detail: localizeFinancialText(localized.detail, language)
  };
}

export function localizeMissions(missions: readonly MissionCatalogItem[], language: Language): readonly MissionCatalogItem[] {
  if (language === 'fr') return missions;
  return missions.map((mission) => {
    const copy = MISSION_COPY[mission.missionId];
    if (!copy) return mission;
    return {
      ...mission,
      title: copy.title,
      hook: copy.hook,
      durationLabel: copy.durationLabel,
      skills: [...copy.skills],
      ...(mission.brief ? { brief: localizeBrief(mission.brief, language) } : {})
    };
  });
}

export function localizeBrief(brief: MissionBrief, language: Language): MissionBrief {
  if (language === 'fr') return brief;
  const copy = MISSION_COPY[brief.missionId];
  if (!copy) return brief;
  return { ...brief, ...copy.brief };
}

export function localizeSnapshot(snapshot: TerminalSnapshot, language: Language): TerminalSnapshot {
  if (language === 'fr') return snapshot;
  return {
    ...snapshot,
    orders: snapshot.orders.map((order) => ({
      ...order,
      rejectReason: order.rejectReason === null ? null : localizeErrorMessage(order.rejectReason, language)
    })),
    messages: snapshot.messages.map(localizeFeedMessage),
    objective: localizeObjective(snapshot.objective),
    fomcWatch: snapshot.fomcWatch === null ? null : {
      ...snapshot.fomcWatch,
      futures: snapshot.fomcWatch.futures.map((future) => ({ ...future })),
      meetings: snapshot.fomcWatch.meetings.map((meeting) => ({
        ...meeting,
        outcomes: meeting.outcomes.map((outcome) => ({ ...outcome, label: exact(outcome.label, FOMC_OUTCOME_TEXT) })),
        previousOutcomes: meeting.previousOutcomes.map((outcome) => ({ ...outcome, label: exact(outcome.label, FOMC_OUTCOME_TEXT) }))
      }))
    }
  };
}

export function localizeHistory(history: readonly HistoryItem[], language: Language): readonly HistoryItem[] {
  if (language === 'fr') return history;
  return history.map((item) => ({
    ...item,
    missionTitle: missionTitle(item.missionId, item.missionTitle),
    label: item.label === null ? null : exact(item.label, LABEL_TEXT)
  }));
}

export function localizeDebrief(debrief: DebriefView, language: Language): DebriefView {
  return {
    ...debrief,
    missionTitle: language === 'en' ? missionTitle(debrief.missionId, debrief.missionTitle) : debrief.missionTitle,
    scoreLabel: debrief.scoreLabel === null ? null : language === 'en' ? exact(debrief.scoreLabel, LABEL_TEXT) : debrief.scoreLabel,
    caps: debrief.caps.map((cap) => ({ ...cap, reason: localizeFinancialText(language === 'en' ? exact(cap.reason, CAP_TEXT) : cap.reason, language) })),
    leads: debrief.leads.map((lead) => ({ ...lead, text: localizeLead(lead.text, language) })),
    metrics: debrief.metrics.map((metric) => localizeMetric(metric, language)),
    timeline: language === 'en' ? debrief.timeline.map(localizeFeedMessage) : debrief.timeline.map((message) => ({ ...message })),
    fills: debrief.fills.map((fill) => ({ ...fill })),
    benchmarks: debrief.benchmarks.map((benchmark) => localizeBenchmark(benchmark, language)),
    comparison: { ...debrief.comparison }
  };
}

export function localizeErrorMessage(message: string, language: Language): string {
  if (language === 'fr') return message;
  const translated = exact(message, ERROR_TEXT);
  if (translated !== message) return translated;

  let match = /^La requête a échoué \((\d+)\)\.$/u.exec(message);
  if (match) return `Request failed (${match[1]}).`;
  match = /^Incident technique\s*:\s*(.+)$/u.exec(message);
  if (match) return `Technical incident: ${localizeErrorMessage(match[1]!, language)}`;
  match = /^version de scénario (.+) indisponible$/u.exec(message);
  if (match) return `Scenario version ${match[1]} is unavailable`;
  match = /^Snapshot incompatible\s*:\s*scénario (.+) \(attendu (.+)\)\.$/u.exec(message);
  if (match) return `Incompatible snapshot: scenario ${match[1]} (expected ${match[2]}).`;
  match = /^Base locale plus récente \((.+)\) que l’application \((.+)\)\. Aucun changement appliqué\.$/u.exec(message);
  if (match) return `The local database is newer (${match[1]}) than the application (${match[2]}). No changes were applied.`;
  match = /^Schéma SQLite inconnu ou incompatible \((.+)\)\. Aucun changement appliqué\.$/u.exec(message);
  if (match) return `Unknown or incompatible SQLite schema (${match[1]}). No changes were applied.`;
  match = /^Commande (.+) absente ou déjà finalisée\.$/u.exec(message);
  if (match) return `Command ${match[1]} is missing or already finalized.`;
  match = /^Commande (.+) réservée sans ACK final\.$/u.exec(message);
  if (match) return `Command ${match[1]} was reserved without a final ACK.`;
  match = /^Configuration incomplète pour (.+)$/u.exec(message);
  if (match) return `Incomplete configuration for ${match[1]}`;
  match = /^Graphique manquant pour (.+)\.$/u.exec(message);
  if (match) return `Missing chart for ${match[1]}.`;
  match = /^Contrat manquant pour le graphique (.+)\.$/u.exec(message);
  if (match) return `Missing contract for chart ${match[1]}.`;
  match = /^Carnet indisponible pour (.+)$/u.exec(message);
  if (match) return `Order book unavailable for ${match[1]}`;
  return message;
}
